import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { JevAutoBudgetView, RankingMigrationView } from '@nest-recruit/shared';
import { ApiError, apiGet, apiPost, apiPut } from '../../../lib/api-client';

// ADR 0062 §6.2, mockup M4: ADMIN-17's "Moving to the ideal candidate" card and "Jev automatic reading". The move's numbers are
// worked out on the server from the asks (never stored, #10); the browser asks again while a job is still being read, because
// the server sends no progress hint for them.

export const MIGRATION_KEY = ['admin', 'ranking-migration'] as const;
export const AUTO_BUDGET_KEY = ['admin', 'jev-auto-budget'] as const;

/** While a job is being read its figures change; this is how often the card asks again. */
const MIGRATION_REFRESH_MS = 10_000;

export function useRankingMigration(): UseQueryResult<RankingMigrationView, ApiError> {
  return useQuery<RankingMigrationView, ApiError>({
    queryKey: MIGRATION_KEY,
    queryFn: () => apiGet<RankingMigrationView>('/admin/ranking-migration'),
    placeholderData: (previous) => previous,
    refetchInterval: (query) => ((query.state.data?.reading ?? 0) > 0 ? MIGRATION_REFRESH_MS : false),
  });
}

/**
 * "Raise limit": the new limit shows at once and the server's numbers follow; a refusal puts the old limit back (§5a rule 1).
 * The limit only goes up.
 */
export function useRaiseMigrationBudget(): UseMutationResult<unknown, ApiError, number, { readonly before: RankingMigrationView | undefined }> {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, number, { readonly before: RankingMigrationView | undefined }>({
    mutationFn: (approvedMicroUsd) => apiPost('/admin/ranking-migration/budget', { approvedMicroUsd }),
    onMutate: async (approvedMicroUsd) => {
      await queryClient.cancelQueries({ queryKey: MIGRATION_KEY });
      const before = queryClient.getQueryData<RankingMigrationView>(MIGRATION_KEY);
      if (before) queryClient.setQueryData<RankingMigrationView>(MIGRATION_KEY, { ...before, approvedMicroUsd });
      return { before };
    },
    onError: (_error, _input, context) => {
      queryClient.setQueryData(MIGRATION_KEY, context?.before);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: MIGRATION_KEY }),
  });
}

/** "Couldn't finish · Retry": the job is queued again, and shows as waiting at once. */
export function useRetryMigrationJob(): UseMutationResult<unknown, ApiError, string, { readonly before: RankingMigrationView | undefined }> {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, string, { readonly before: RankingMigrationView | undefined }>({
    mutationFn: (jobId) => apiPost(`/admin/ranking-migration/jobs/${jobId}/retry`, {}),
    onMutate: async (jobId) => {
      await queryClient.cancelQueries({ queryKey: MIGRATION_KEY });
      const before = queryClient.getQueryData<RankingMigrationView>(MIGRATION_KEY);
      if (before) {
        queryClient.setQueryData<RankingMigrationView>(MIGRATION_KEY, {
          ...before,
          failed: Math.max(0, before.failed - 1),
          waitingForBudget: before.waitingForBudget + 1,
          jobs: before.jobs.map((job) => (job.jobId === jobId ? { ...job, status: 'waiting_for_budget' } : job)),
        });
      }
      return { before };
    },
    onError: (_error, _input, context) => {
      queryClient.setQueryData(MIGRATION_KEY, context?.before);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: MIGRATION_KEY }),
  });
}

export function useJevAutoBudget(): UseQueryResult<JevAutoBudgetView, ApiError> {
  return useQuery<JevAutoBudgetView, ApiError>({
    queryKey: AUTO_BUDGET_KEY,
    queryFn: () => apiGet<JevAutoBudgetView>('/admin/jev-auto-budget'),
    placeholderData: (previous) => previous,
  });
}

/** "Edit daily limit": the new limit shows at once; a refusal puts the old one back. */
export function useSetJevAutoBudget(): UseMutationResult<unknown, ApiError, number, { readonly before: JevAutoBudgetView | undefined }> {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, number, { readonly before: JevAutoBudgetView | undefined }>({
    mutationFn: (dailyMicroUsd) => apiPut('/admin/jev-auto-budget', { dailyMicroUsd }),
    onMutate: async (dailyMicroUsd) => {
      await queryClient.cancelQueries({ queryKey: AUTO_BUDGET_KEY });
      const before = queryClient.getQueryData<JevAutoBudgetView>(AUTO_BUDGET_KEY);
      if (before) queryClient.setQueryData<JevAutoBudgetView>(AUTO_BUDGET_KEY, { ...before, dailyMicroUsd, isPaused: before.spentTodayMicroUsd >= dailyMicroUsd });
      return { before };
    },
    onError: (_error, _input, context) => {
      queryClient.setQueryData(AUTO_BUDGET_KEY, context?.before);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: AUTO_BUDGET_KEY }),
  });
}
